/**
 * Centralized Firebase Analytics Service for Web
 * Implements PO Event Shipping Checklist specifications with strict typing
 * Matches Mobile analytics.service.ts 100% in events and parameters
 * @module services/analytics.service
 */

import {
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  logEvent,
} from 'firebase/analytics';
import { getFirebaseAnalytics } from '../config/firebase';

// =============================================================================
// ENUMS & TYPES
// =============================================================================

export type LiveConversationMode = 'video' | 'voice_text';

export type WaitingRoomExitReason =
  | 'connected'
  | 'user_cancel'
  | 'timeout'
  | 'error'
  | 'capacity_full';

export type ConversationEndReason =
  | 'user'
  | 'credits_exhausted'
  | 'time_limit'
  | 'error'
  | 'disconnect';

export type PaywallSource =
  | 'navbar'
  | 'credits_exhausted'
  | 'daily_limit'
  | 'settings_usage'
  | 'bottom_nav'
  | 'onboarding'
  | 'sahabe_profile'
  | 'profile_upgrade'
  | string;

export type ItemType = 'subscription' | 'credit_addon' | 'sponsorship';

export type AuthMethod = 'email' | 'google' | 'apple';

export type PermissionType = 'mic' | 'camera' | 'notification';

export type DailyLimitType = 'daily_text' | 'credits';

export type PaywallCloseReason =
  | 'not_purchased'
  | 'purchased_subscription'
  | 'purchased_addon_credits'
  | 'redirected_to_login'
  | 'dismissed'
  | 'back'
  | 'purchased'
  | 'error'
  | string;

// Environment check
const isDev =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENVIRONMENT === 'dev' ||
  !process.env.NODE_ENV;

// Event deduplicator to prevent duplicate firing across renders/Strict Mode
const lastLoggedEvents = new Map<string, number>();

const sanitizeParam = (val: any): any => {
  if (val === null || val === undefined) return val;
  if (typeof val === 'number' || typeof val === 'boolean') return val;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    if ('nativeEvent' in val || 'target' in val || 'currentTarget' in val) {
      return 'event';
    }
    try {
      JSON.stringify(val);
      return val;
    } catch {
      return String(val);
    }
  }
  return String(val);
};

let lastScreenName = '';
let lastScreenTimestamp = 0;
const SCREEN_VIEW_DEDUPE_MS = 1500;

const safeLogEvent = async (eventName: string, params?: Record<string, any>) => {
  try {
    const eventParams = params || {};
    const sanitizedParams: Record<string, any> = {};
    for (const key of Object.keys(eventParams)) {
      sanitizedParams[key] = sanitizeParam(eventParams[key]);
    }
    // Normalize params key for robust deduplication (sort keys so order doesn't matter)
    const sortedKeys = Object.keys(sanitizedParams).sort();
    const sortedParams: Record<string, any> = {};
    for (const key of sortedKeys) {
      sortedParams[key] = sanitizedParams[key];
    }
    const eventKey = `${eventName}:${JSON.stringify(sortedParams)}`;
    const now = Date.now();
    const lastTime = lastLoggedEvents.get(eventKey);
    // Deduplicate identical events within 1500ms
    if (lastTime && now - lastTime < 1500) {
      return;
    }
    lastLoggedEvents.set(eventKey, now);

    // Prune old entries from map
    if (lastLoggedEvents.size > 100) {
      lastLoggedEvents.forEach((time, k) => {
        if (now - time > 10000) lastLoggedEvents.delete(k);
      });
    }

    const analytics = await getFirebaseAnalytics();
    const payload = isDev ? { ...sanitizedParams, debug_mode: true } : sanitizedParams;
    if (analytics) {
      (logEvent as (inst: any, name: string, p?: any) => void)(analytics, eventName, payload);
    }
  } catch {
    // Silently ignore tracking errors to avoid logging sensitive analytics data in the browser console
  }
};

// =============================================================================
// EVENT TRACKING METHODS
// =============================================================================

/**
 * 1. screen_view
 * Tracks screen views including special PO screens:
 * LiveConversation_*, WaitingRoom, ConversationEnd, MicPermission, Settings_Usage
 */
export const trackScreenView = async (screenName: string) => {
  if (!screenName) return;
  const now = Date.now();
  if (lastScreenName === screenName && now - lastScreenTimestamp < SCREEN_VIEW_DEDUPE_MS) {
    return;
  }
  lastScreenName = screenName;
  lastScreenTimestamp = now;

  await safeLogEvent('screen_view', {
    screen_name: screenName,
    screen_class: screenName,
    page_title: `TheyChat | ${screenName}`,
  });
};

/**
 * 2. sahabe_select
 * Tracks sahabe card or profile selection with search context and index
 */
export const trackSahabeSelect = async (
  sahabeId: string,
  source: 'home' | 'sahabes_list' | 'sahabe_profile' | 'recent_chat' | 'home_search' | 'sponsor_search' | string,
  extra?: {
    isSearch?: boolean;
    searchQuery?: string;
    index?: number;
    totalSeen?: number;
  },
) => {
  await safeLogEvent('sahabe_select', {
    sahabe_id: sahabeId,
    source,
    ...(extra?.isSearch !== undefined ? { is_search: extra.isSearch } : {}),
    ...(extra?.searchQuery ? { search_query: extra.searchQuery } : {}),
    ...(extra?.index !== undefined ? { item_index: extra.index } : {}),
    ...(extra?.totalSeen !== undefined ? { total_seen: extra.totalSeen } : {}),
  });
};

/**
 * search
 * Tracks companion search queries and results count
 */
export const trackSearch = async (params: {
  searchTerm: string;
  resultsCount: number;
  screenName: string;
}) => {
  if (!params.searchTerm || params.searchTerm.trim().length === 0) return;
  await safeLogEvent('search', {
    search_term: params.searchTerm.trim(),
    number_of_results: params.resultsCount,
    screen_name: params.screenName,
  });
};

/**
 * 3. conversation_intent
 * Tracks user intention to start conversation before connecting/queuing
 */
export const trackConversationIntent = async (
  sahabeId: string,
  mode: LiveConversationMode,
  creditsBalance: number,
) => {
  await safeLogEvent('conversation_intent', {
    sahabe_id: sahabeId,
    mode,
    credits_balance: Math.round(creditsBalance),
  });
};

/**
 * 4. waiting_room_enter
 * Tracks user entering the HeyGen / avatar waiting queue
 */
export const trackWaitingRoomEnter = async (
  sahabeId: string,
  conversationId: string,
  mode: LiveConversationMode,
) => {
  await safeLogEvent('waiting_room_enter', {
    sahabe_id: sahabeId,
    conversation_id: conversationId,
    mode,
  });
};

/**
 * 5. ui_tap
 * Tracks critical UI button / action taps
 */
export const trackUiTap = async (screenName: string, elementId: string, elementLabel: string) => {
  await safeLogEvent('ui_tap', {
    screen_name: screenName,
    element_id: elementId,
    element_label: elementLabel,
  });
};

/**
 * 6. waiting_room_exit
 * Tracks user exiting queue with wait duration and reason
 */
export const trackWaitingRoomExit = async (waitSeconds: number, reason: WaitingRoomExitReason) => {
  await safeLogEvent('waiting_room_exit', {
    wait_seconds: Math.max(0, Math.round(waitSeconds)),
    reason,
  });
};

/**
 * 7. conversation_start
 * Tracks live session start
 */
export const trackConversationStart = async (params: {
  sahabeId: string;
  conversationId: string;
  mode: LiveConversationMode;
  creditsBalance: number;
  waitSeconds: number;
}) => {
  const isVideo = params.mode === 'video';
  await safeLogEvent('conversation_start', {
    sahabe_id: params.sahabeId,
    conversation_id: params.conversationId,
    mode: params.mode,
    chat_type: isVideo ? 'video' : 'text',
    credits_balance: Math.round(params.creditsBalance),
    wait_seconds: Math.max(0, Math.round(params.waitSeconds)),
  });
};

/**
 * 8. conversation_end
 * Tracks live session termination
 */
export const trackConversationEnd = async (params: {
  sahabeId: string;
  conversationId: string;
  mode: LiveConversationMode;
  creditsUsed: number;
  endReason: ConversationEndReason;
  durationSeconds?: number;
  msgCount?: number;
}) => {
  const isVideo = params.mode === 'video';
  const count = params.msgCount ?? 0;
  const cleanReason =
    typeof params.endReason === 'string' && params.endReason.length > 0
      ? params.endReason
      : 'user';
  await safeLogEvent('conversation_end', {
    sahabe_id: params.sahabeId,
    conversation_id: params.conversationId,
    mode: params.mode,
    chat_type: isVideo ? 'video' : 'text',
    credits_used: Math.max(0, Math.round(params.creditsUsed * 10) / 10),
    end_reason: cleanReason,
    ...(params.durationSeconds !== undefined ? { duration_seconds: params.durationSeconds } : {}),
    msg_count: count,
    ...(isVideo
      ? { video_msg_count: count, text_msg_count: 0 }
      : { text_msg_count: count, video_msg_count: 0 }),
  });
};

/**
 * 9. credits_exhausted
 * Tracks when user runs out of credits
 */
export const trackCreditsExhausted = async (
  screenName: string,
  creditsUsedTotal: number,
  planType: string,
) => {
  await safeLogEvent('credits_exhausted', {
    screen_name: screenName,
    credits_used_total: Math.max(0, Math.round(creditsUsedTotal * 10) / 10),
    plan_type: planType || 'free',
  });
};

/**
 * 10. daily_limit_hit
 * Tracks when daily limit is reached
 */
export const trackDailyLimitHit = async (limitType: DailyLimitType, screenName: string) => {
  await safeLogEvent('daily_limit_hit', {
    limit_type: limitType,
    screen_name: screenName,
  });
};

/**
 * 11. permission_result
 * Tracks permission prompt result
 */
export const trackPermissionResult = async (
  permission: PermissionType,
  granted: boolean,
  screenName: string,
) => {
  await safeLogEvent('permission_result', {
    permission,
    granted: !!granted,
    screen_name: screenName,
  });
};

/**
 * 12. error_shown
 * Tracks in-app user-visible errors
 */
export const trackErrorShown = async (
  screenName: string,
  errorCode: string,
  errorSource: string,
  fatal: boolean,
) => {
  await safeLogEvent('error_shown', {
    screen_name: screenName,
    error_code: errorCode,
    error_source: errorSource,
    fatal: !!fatal,
  });
};

/**
 * 13. purchase
 * Tracks successful purchases (subscription, credits, sponsorship)
 */
export const trackPurchase = async (params: {
  planId: string;
  value: number;
  currency: string;
  transactionId: string;
  itemType?: ItemType;
  credits?: number;
}) => {
  await safeLogEvent('purchase', {
    plan_id: params.planId,
    value: Number(params.value.toFixed(2)),
    currency: params.currency || 'USD',
    transaction_id: params.transactionId,
    ...(params.itemType ? { item_type: params.itemType } : {}),
    ...(params.credits !== undefined ? { credits: params.credits } : {}),
  });
};

/**
 * 14. paywall_viewed
 * Tracks paywall/subscription screen views
 */
export const trackPaywallViewed = async (
  source: PaywallSource,
  planType: string,
  creditsBalance?: number,
) => {
  await safeLogEvent('paywall_viewed', {
    screen_name: 'Subscription',
    page_title: 'TheyChat | Subscription',
    source: source || 'navbar',
    plan_type: planType || 'free',
    ...(creditsBalance !== undefined
      ? { credits_balance: Math.max(0, Math.round(creditsBalance * 10) / 10) }
      : {}),
  });
};

/**
 * 21. subscription_plan_click
 * Tracks when user clicks on a subscription tier card / button
 */
export const trackSubscriptionPlanClick = async (params: {
  planId: string;
  planName: string;
  price?: number;
  currency?: string;
  currentPlan?: string;
  actionType?: 'upgrade' | 'downgrade' | 'subscribe' | 'select';
  source?: string;
}) => {
  await safeLogEvent('subscription_plan_click', {
    plan_id: params.planId,
    plan_name: params.planName,
    price: params.price !== undefined ? Number(params.price.toFixed(2)) : 0,
    currency: params.currency || 'USD',
    current_plan: params.currentPlan || 'free',
    action_type: params.actionType || 'subscribe',
    source: params.source || 'subscription_screen',
  });
};

/**
 * 27. paywall_plan_selected
 * Tracks which pricing plan was selected on the paywall screen
 */
export const trackPaywallPlanSelected = async (params: {
  planId: string;
  priceTier?: string | number;
}) => {
  await safeLogEvent('paywall_plan_selected', {
    plan_id: params.planId,
    price_tier: params.priceTier !== undefined ? String(params.priceTier) : params.planId,
  });
};

/**
 * 28. paywall_closed
 * Tracks when and why user closed or exited the paywall screen without purchasing or after action
 */
export const trackPaywallClosed = async (params: {
  reason: PaywallCloseReason;
  planViewed?: string;
  secondsOnScreen: number;
}) => {
  await safeLogEvent('paywall_closed', {
    screen_name: 'Subscription',
    reason: params.reason || 'not_purchased',
    ...(params.planViewed ? { plan_viewed: params.planViewed } : {}),
    seconds_on_screen: Math.max(0, Math.round(params.secondsOnScreen)),
  });
};

/**
 * 29. plan_changed
 * Tracks subscription plan upgrades, downgrades, or cross-tier changes
 */
export const trackPlanChanged = async (params: {
  fromPlan: string;
  toPlan: string;
  source: string;
}) => {
  await safeLogEvent('plan_changed', {
    from_plan: params.fromPlan || 'free',
    to_plan: params.toPlan,
    source: params.source || 'subscription',
  });
};

/**
 * 22. addon_credit_click
 * Tracks when user clicks to buy an addon credit pack
 */
export const trackAddonCreditClick = async (params: {
  packId: string;
  credits: number;
  price?: number;
  currency?: string;
  currentPlan?: string;
  source?: string;
}) => {
  await safeLogEvent('addon_credit_click', {
    pack_id: params.packId,
    credits: params.credits,
    price: params.price !== undefined ? Number(params.price.toFixed(2)) : 0,
    currency: params.currency || 'USD',
    current_plan: params.currentPlan || 'free',
    source: params.source || 'subscription_screen',
  });
};

/**
 * 15. sign_up / login
 * Tracks authentication events
 */
export const trackSignUp = async (method: AuthMethod) => {
  await safeLogEvent('sign_up', { method });
};

export const trackLogin = async (method: AuthMethod) => {
  await safeLogEvent('login', { method });
};

/**
 * 16. feedback_submitted
 * Tracks feedback form submissions
 */
export const trackFeedbackSubmitted = async (screenName: string, hasText: boolean) => {
  await safeLogEvent('feedback_submitted', {
    screen_name: screenName,
    has_text: !!hasText,
  });
};

/**
 * 17. sponsor_sahabe_view
 * Tracks when a user views/inspects a companion's sponsorship details
 */
export const trackSponsorSahabeView = async (params: {
  sahabeId: string;
  sahabeName?: string;
  isSponsored?: boolean;
  source?: string;
}) => {
  await safeLogEvent('sponsor_sahabe_view', {
    sahabe_id: params.sahabeId,
    ...(params.sahabeName ? { sahabe_name: params.sahabeName } : {}),
    is_sponsored: !!params.isSponsored,
    source: params.source || 'sponsor_list',
  });
};

/**
 * 18. sponsor_button_click
 * Tracks when a user clicks 'Sponsor Ol' / 'Donate Now' button for a companion
 */
export const trackSponsorButtonClick = async (params: {
  sahabeId: string;
  sahabeName?: string;
  isSponsored?: boolean;
  screenName: string;
  source?: string;
}) => {
  await safeLogEvent('sponsor_button_click', {
    sahabe_id: params.sahabeId,
    ...(params.sahabeName ? { sahabe_name: params.sahabeName } : {}),
    is_sponsored: !!params.isSponsored,
    screen_name: params.screenName,
    source: params.source || 'button',
  });
};

/**
 * 19. upgrade_plan_click
 * Tracks when a user clicks 'Upgrade Plan' button
 */
export const trackUpgradePlanClick = async (params: { source: string; currentPlan?: string }) => {
  await safeLogEvent('upgrade_plan_click', {
    source: params.source || 'settings_usage',
    current_plan: params.currentPlan || 'free',
  });
};

/**
 * 20. settings_tab_view
 * Tracks which tab (usage vs account vs password) the user views in Settings
 */
export const trackSettingsTabView = async (tabName: 'usage' | 'account' | 'password') => {
  await safeLogEvent('settings_tab_view', {
    tab_name: tabName,
  });
};

/**
 * 23. sponsor_select
 * Tracks when a user selects / navigates to a sponsor / sponsored companion
 */
export const trackSponsorSelect = async (params: {
  sponsorId: string;
  source: 'sponsor_list' | 'sahabe_profile' | 'home_banner' | string;
}) => {
  await safeLogEvent('sponsor_select', {
    sponsor_id: params.sponsorId,
    source: params.source,
  });
};

/**
 * 24. sponsor_action
 * Tracks user interactions with sponsor actions (website, phone, map, social, share, cta)
 */
export const trackSponsorAction = async (params: {
  sponsorId: string;
  action: 'website' | 'phone' | 'map' | 'social' | 'share' | 'video_play' | 'cta' | string;
  elementId: string;
}) => {
  await safeLogEvent('sponsor_action', {
    sponsor_id: params.sponsorId,
    action: params.action,
    element_id: params.elementId,
  });
};

/**
 * 25. sponsor_impression
 * Tracks when a sponsor card/banner is rendered and viewed in screens (e.g. home, sahabe profile)
 */
export const trackSponsorImpression = async (params: {
  sponsorId: string;
  screenName: string;
  position?: number | string;
}) => {
  await safeLogEvent('sponsor_impression', {
    sponsor_id: params.sponsorId,
    screen_name: params.screenName,
    ...(params.position !== undefined ? { position: params.position } : {}),
  });
};

/**
 * 26. list_scroll
 * Tracks the deepest visible item index and total companions inspected in a list/carousel (Home, SponsorList).
 * Logged once upon screen exit/blur.
 */
export const trackListScroll = async (
  screenNameOrParams:
    | string
    | {
        screenName: string;
        maxIndexSeen: number;
        totalItemsSeen?: number;
        totalItems?: number;
      },
  legacyMaxIndex?: number,
) => {
  const screenName =
    typeof screenNameOrParams === 'string'
      ? screenNameOrParams
      : screenNameOrParams.screenName;

  const maxIndex =
    typeof screenNameOrParams === 'string'
      ? legacyMaxIndex ?? 0
      : screenNameOrParams.maxIndexSeen;

  const totalSeen =
    typeof screenNameOrParams === 'object'
      ? screenNameOrParams.totalItemsSeen
      : undefined;

  const total =
    typeof screenNameOrParams === 'object'
      ? screenNameOrParams.totalItems
      : undefined;

  await safeLogEvent('list_scroll', {
    screen_name: screenName,
    page_title: `TheyChat | ${screenName}`,
    max_index_seen: Math.max(0, maxIndex),
    ...(totalSeen !== undefined ? { total_items_seen: totalSeen } : {}),
    ...(total !== undefined ? { total_items: total } : {}),
  });
};

/**
 * 30. screen_time
 * Tracks exact duration spent on a specific screen when user transitions to another screen or exits
 */
export const trackScreenTime = async (params: {
  screenName: string;
  durationSeconds: number;
  nextScreen?: string;
}) => {
  if (!params.screenName || params.durationSeconds < 0.5) return;
  await safeLogEvent('screen_time', {
    screen_name: params.screenName,
    page_title: `TheyChat | ${params.screenName}`,
    duration_seconds: Math.max(1, Math.round(params.durationSeconds)),
    ...(params.nextScreen ? { next_screen: params.nextScreen } : {}),
  });
};

// =============================================================================
// USER PROPERTIES & USER ID
// =============================================================================

/**
 * Helper to compute credits bucket (e.g. '0', '1-10', '11-50', '51-100', '100+')
 */
export const getCreditsBucket = (credits: number | null | undefined): string => {
  if (credits === null || credits === undefined || credits <= 0) return '0';
  if (credits <= 10) return '1-10';
  if (credits <= 50) return '11-50';
  if (credits <= 100) return '51-100';
  return '100+';
};

/**
 * Updates User Properties in Firebase Analytics
 */
export const updateUserProperties = async (properties: {
  planType?: string;
  creditsBalanceBucket?: string;
  orgId?: string;
  language?: string;
}) => {
  try {
    const analytics = await getFirebaseAnalytics();
    if (!analytics) return;

    const userProps: Record<string, string> = {};
    if (properties.planType !== undefined) {
      userProps.plan_type = properties.planType;
    }
    if (properties.creditsBalanceBucket !== undefined) {
      userProps.credits_balance_bucket = properties.creditsBalanceBucket;
    }
    if (properties.orgId !== undefined) {
      userProps.org_id = properties.orgId;
    }
    if (properties.language !== undefined) {
      userProps.language = properties.language;
    }

    firebaseSetUserProperties(analytics, userProps);
  } catch {
    // Silently ignore tracking errors to avoid logging sensitive analytics data in the browser console
  }
};

/**
 * Sets or clears the User ID in Firebase Analytics
 */
export const setAnalyticsUserId = async (userId: string | null) => {
  try {
    const analytics = await getFirebaseAnalytics();
    if (!analytics) return;

    firebaseSetUserId(analytics, userId);
  } catch {
    // Silently ignore tracking errors to avoid logging sensitive analytics data in the browser console
  }
};
